import { Box, Paper, Container, Fade, Alert, Typography, Card } from '@mui/material'
import TableHeaderTitle from '../../components/TableHeaderTitle'
import UserActions from './users/actions/UserActions'
import UsersList from './users/components/UsersList'
import { useState } from 'react'
import { useMultiplePermissions } from '../../hooks/usePermission'
import FintechLoader from '../../components/FintechLoader'
import {
    Security as SecurityIcon,
} from '@mui/icons-material';

const UsersPage = () => {
    const [userModalOpen, setUserModalOpen] = useState(false);

    // Kullanıcı yetkileri kontrolü
    // Not: KullanıcıyaRolAtama yetkisi UsersList içinde, liste yüklendikten sonra kontrol edilecek
    const permissions = useMultiplePermissions([
        'Corporate.POST.Reading.KullanıcıListesineErişim',     // Kullanıcı listesini görüntüleme yetkisi
        'Corporate.POST.Reading.KullanıcıEklemeYetkisi'       // Kullanıcı ekleme yetkisi
    ]);

    const canViewUserList = permissions['Corporate.POST.Reading.KullanıcıListesineErişim']?.hasPermission ?? false;
    const canAddUser = permissions['Corporate.POST.Reading.KullanıcıEklemeYetkisi']?.hasPermission;

    // Loading durumlarını kontrol et
    const isLoadingPermissions = permissions['Corporate.POST.Reading.KullanıcıListesineErişim']?.isLoading ||
        permissions['Corporate.POST.Reading.KullanıcıEklemeYetkisi']?.isLoading;

    const handleAddUserClick = () => {
        if (canAddUser) {
            setUserModalOpen(true);
        }
    };

    // Yetki kontrolleri yüklenene kadar sayfa API'lerini tetikleme
    if (isLoadingPermissions) {
        return (
            <Container maxWidth={false} sx={{
                flexGrow: 1,
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                height: 'calc(100vh - 120px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <FintechLoader />
                </Box>
            </Container>
        );
    }

    // Eğer kullanıcı listesini görüntüleme yetkisi yoksa, erişim engelle
    if (!canViewUserList) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Card sx={{
                    maxWidth: 500,
                    p: 4,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)',
                    background: 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(252, 165, 165, 0.3)',
                    textAlign: 'center'
                }}>
                    <SecurityIcon sx={{ fontSize: 64, color: '#ef4444', mb: 3 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>
                        Erişim Engellendi
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
                        Bu sayfaya erişim yetkiniz bulunmamaktadır. Sistem yöneticinizle iletişime geçiniz.
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <Container maxWidth={false} sx={{
            flexGrow: 1,
            padding: { xs: '110px 8px 0 8px', sm: '110px 12px 0 12px', md: '110px 32px 0 32px', lg: '110px 40px 0 40px' },
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            minHeight: '100vh'
        }}>
            {/* Main Dashboard Container */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 25%, #3b82f6 50%, #2a5298 75%, #1e3c72 100%)',
                    }
                }}
            >
                <Box sx={{
                    py: { xs: 2, sm: 3, md: 4 },
                    pb: 2,
                    px: 0,
                    background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.03) 0%, rgba(42, 82, 152, 0.06) 100%)',
                    borderBottom: '1px solid rgba(226, 232, 240, 0.8)'
                }}>
                    <Fade in={true} timeout={600}>
                        <Box sx={{ px: { xs: 2, sm: 3 }, mx: { xs: 0, sm: 2 } }}>
                            <UserActions
                                onAddUserClick={handleAddUserClick}
                                canAddUser={canAddUser}
                            />
                            <Paper
                                elevation={0}
                                sx={{
                                    mt: 3,
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.7)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(226, 232, 240, 0.8)',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}
                            >
                                {/* Table Header */}
                                <Box sx={{
                                    p: 3,
                                    pb: 2,
                                    background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.9) 100%)',
                                    borderBottom: '1px solid rgba(226, 232, 240, 0.6)'
                                }}>
                                    <TableHeaderTitle title="Kullanıcı Listesi" />
                                </Box>

                                {/* Table Content */}
                                <Box sx={{ p: 3 }}>
                                    <UsersList
                                        isLoadingPermissions={isLoadingPermissions}
                                        userModalOpen={userModalOpen}
                                        setUserModalOpen={setUserModalOpen}
                                        canViewUserList={canViewUserList}
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    </Fade>
                </Box>

            </Paper>
        </Container>
    )
}

export default UsersPage;
